import React from 'react';
import styles from './CalloutBanner.module.css';
import Icon from '../Icon';
import { cva } from 'class-variance-authority';
import IconButton from '../IconButton';
import {
  generateResponsiveClasses,
  getBaseValueOrUndefined,
  WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const getResponsiveClasses = generateResponsiveClasses(styles);

const calloutBannerClasses = cva(styles['callout-banner'], {
  variants: {
    emphasis: {
      primary: styles['callout-banner--primary'],
      secondary: styles['callout-banner--secondary'],
      tertiary: styles['callout-banner--tertiary'],
    },
    styleVariant: {
      critical: styles['callout-banner--critical'],
      success: styles['callout-banner--success'],
      information: styles['callout-banner--information'],
      neutral: styles['callout-banner--neutral'],
      accent: styles['callout-banner--accent'],
    },
  },
  defaultVariants: {
    emphasis: 'primary',
    styleVariant: 'critical',
  },
});

type StyleVariant =
  | 'critical'
  | 'success'
  | 'information'
  | 'neutral'
  | 'accent';

type DismissButtonProps = Omit<
  React.ComponentProps<typeof IconButton>,
  | 'onPress'
  | 'iconProps'
  | 'size'
  | 'styleVariant'
  | 'emphasis'
  | 'tooltipLabel'
>;

export interface CalloutBannerBaseProps {
  /**
   * The main heading text of the banner
   */
  title: string;
  /**
   * Optional secondary text providing more details
   * Can be a string or React node
   */
  description?: string | React.ReactNode;
  /**
   * Optional emphasis style for the banner
   * @default 'primary'
   */
  emphasis?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Optional style variant for the banner
   * - 'critical' for errors, urgent warnings
   * - 'success' for confirmations, achievements
   * - 'information' for For general updates, tips
   * - 'neutral' for non-critical messages, guidance
   * - 'accent' for any brand specific promotions or callouts
   * @default 'critical'
   */
  styleVariant?: StyleVariant;
  /**
   * Optional button group to be rendered as a label
   * @deprecated Use CalloutBanner.Action instead
   */
  label?: React.ReactNode;
  /**
   * Optional label for the dismiss button tooltip
   */
  dismissButtonTooltipLabel?: string;
  /**
   * Optional props for the dismiss icon button
   */
  dismissButtonProps?: DismissButtonProps;
  /**
   * Optional content to be rendered in the end section of the banner
   */
  children?: React.ReactNode;
  /**
   * Semantic heading level to use when styleVariant is 'heading'
   * Affects accessibility and document structure
   * @default 'h3'
   */
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Optional callback function triggered when the dismiss button is clicked
   */
  onDismiss?: () => void;
  /**
   * Optional custom asset to be rendered instead of the default icon.
   * Can use another icon, image, or any React node.
   */
  customAsset?: React.ReactNode;
  /**
   * Optional prop to hide the illustration
   * @default false
   */
  hideIllustration?: boolean;
  /**
   * Optional prop to align buttons within the banner
   * - 'left' aligns buttons to the left
   * - 'right' aligns buttons to the right
   * @default 'left'
   * @deprecated Use CalloutBanner.Action instead
   */
  alignButtons?: 'left' | 'right';
  /**
   * Layout of the body content
   * - 'row' displays body content in a horizontal row
   * - 'column' displays body content in a vertical column
   * @default 'row'
   */
  headerLayout?: 'row' | 'column';
  /**
   * Optional prefix text to be added before the title to denote severity.
   * E.g., "Critical", "Success", "Information"
   * For i18m purposes, this should be provided by the consumer.
   */
  severityLabel?: string;
  /**
   * Optional ARIA role for the banner. Defaults to 'alert' for critical, 'status' for success/information, 'region' for neutral.
   */
  role?: React.AriaRole;
  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export type CalloutBannerAriaProps = CalloutBannerBaseProps &
  React.AriaAttributes;

type ResponsivePropKeys = 'headerLayout';

export type CalloutBannerProps = WithResponsiveProps<
  CalloutBannerAriaProps,
  ResponsivePropKeys
>;

const CALLOUT_BANNER_META = {
  critical: {
    icon: 'circle-exclamation',
    severityLabel: 'Critical',
    role: 'alert',
  },
  success: {
    icon: 'check-circle',
    severityLabel: 'Success',
    role: 'status',
  },
  information: {
    icon: 'info-circle',
    severityLabel: 'Information',
    role: 'status',
  },
  neutral: {
    icon: 'check-circle',
    severityLabel: '',
    role: 'region',
  },
  accent: {
    icon: 'house',
    severityLabel: '',
    role: 'region',
  },
} as const satisfies Record<
  StyleVariant,
  { icon: string; severityLabel: string; role: string }
>;

export const CalloutBanner = ({
  title,
  description,
  label,
  children,
  styleVariant = 'critical',
  emphasis = 'primary',
  headingLevel = 'h3',
  dismissButtonTooltipLabel,
  onDismiss,
  customAsset,
  hideIllustration = false,
  alignButtons = 'left',
  headerLayout = 'column',
  severityLabel,
  role,
  dismissButtonProps = {
    'aria-label': 'Dismiss banner',
  },
  'data-testid': dataTestId,
  ...ariaProps
}: CalloutBannerProps) => {
  const hasOnDismiss = typeof onDismiss === 'function';
  const {
    icon: iconName,
    severityLabel: defaultSeverityLabel,
    role: bannerRole,
  } = CALLOUT_BANNER_META[styleVariant];

  const HeadingBanner = headingLevel;
  const titleId = React.useId();
  const descriptionId = React.useId();
  const resolvedSeverityLabel = severityLabel ?? defaultSeverityLabel;
  const resolvedRole = role || bannerRole;

  const baseHeaderLayout = getBaseValueOrUndefined(headerLayout);

  const headerLayoutResp = getResponsiveClasses(
    headerLayout,
    'callout-banner--header-layout',
  );

  // Get children, and separate out CalloutBanner.Action if present with body children
  const childrenArray = React.Children.toArray(children);

  // CalloutBanner.Action children
  const action = childrenArray.find((child) => {
    return React.isValidElement(child) && child.type === CalloutBannerAction;
  }) as React.ReactElement<CalloutBannerActionProps> | undefined;

  // Body children (excluding CalloutBanner.Action)
  const bodyChildren = childrenArray.filter((child) => {
    return !(React.isValidElement(child) && child.type === CalloutBannerAction);
  });

  const renderDescriptionContent = () => {
    if (!description) return null;
    return description;
  };

  const onlyTitle = !description;

  return (
    <div
      className={[
        calloutBannerClasses({
          emphasis,
          styleVariant,
        }),
      ]
        .filter(Boolean)
        .join(' ')}
      role={resolvedRole}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      data-testid={dataTestId}
      {...ariaProps}
    >
      <div className={styles['callout-banner__start']}>
        {/* Allow users to change this icon */}
        {!hideIllustration &&
          (customAsset ? (
            <div className={styles['callout-banner__custom-asset']}>
              {customAsset}
            </div>
          ) : (
            <div
              className={
                onlyTitle
                  ? styles['callout-banner__default-asset--center']
                  : undefined
              }
            >
              <Icon
                iconName={iconName}
                iconPrefix="fas"
                className={styles['callout-banner__icon']}
                iconSize="1.25x"
                padding="roomy"
              />
            </div>
          ))}
        <div className={styles['callout-banner__meta']}>
          {hasOnDismiss && (
            <div className={styles['callout-banner__dismiss']}>
              <IconButton
                onPress={onDismiss}
                styleVariant="neutral"
                emphasis={emphasis === 'tertiary' ? 'tertiary' : 'primary'}
                size="sm"
                iconProps={{
                  iconName: 'close',
                  iconPrefix: 'fas',
                  iconSize: '1x',
                }}
                tooltipLabel={dismissButtonTooltipLabel}
                {...dismissButtonProps}
              />
            </div>
          )}
          <div
            className={[
              styles['callout-banner__meta-content'],
              alignButtons === 'right'
                ? styles['callout-banner__meta-right']
                : '',
              baseHeaderLayout === 'row'
                ? styles['callout-banner--header-layout--row']
                : '',
              ...headerLayoutResp,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div>
              <div
                className={`${styles['callout-banner__meta-top']} ${
                  hasOnDismiss ? styles['callout-banner__meta-top-dismiss'] : ''
                }`}
              >
                <HeadingBanner
                  id={titleId}
                  className={styles['callout-banner__meta-title']}
                >
                  <span
                    className={styles['callout-banner__severity-label']}
                    dir="auto"
                  >
                    {resolvedSeverityLabel}
                    {resolvedSeverityLabel ? ': ' : ''}
                  </span>
                  <bdi dir="auto">{title}</bdi>
                </HeadingBanner>
                {description && (
                  <p
                    id={descriptionId}
                    className={styles['callout-banner__meta-description']}
                  >
                    {renderDescriptionContent()}
                  </p>
                )}
              </div>
            </div>
            {label && (
              <div className={styles['callout-banner__meta-label']}>
                {label}
              </div>
            )}
            {action && (
              <div className={styles['callout-banner__meta-label']}>
                {action.props.children}
              </div>
            )}
          </div>
        </div>
      </div>
      {bodyChildren.length > 0 && (
        <div className={styles['callout-banner__end']}>{bodyChildren}</div>
      )}
    </div>
  );
};

/***************************************
 * CalloutBanner.Action component
 ***************************************/

export type CalloutBannerActionProps = {
  /**
   * Children nodes to be rendered inside the CalloutBanner.Action
   */
  children: React.ReactNode;
};

export const CalloutBannerAction = ({ children }: CalloutBannerActionProps) => {
  return <div className={styles['callout-banner__meta-label']}>{children}</div>;
};

CalloutBanner.Action = CalloutBannerAction;
CalloutBannerAction.displayName = 'CalloutBanner.Action';

export default CalloutBanner;
